import type { Database, Json } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type BitrixConfig = {
  source_ids: Record<string, string>;
  uf_field_map: Record<string, string>;
  retry: {
    max_attempts: number;
    backoff_seconds: number[];
  };
  webhook_timeout_ms: number;
  lock_seconds: number;
  batch_size: number;
};

export type BitrixSettingsRow = Pick<
  Database['public']['Tables']['integration_settings']['Row'],
  | 'name'
  | 'enabled'
  | 'config'
  | 'last_test_at'
  | 'last_test_status'
  | 'last_test_error'
  | 'updated_at'
>;

export type BitrixSettings = Omit<BitrixSettingsRow, 'config'> & {
  config: BitrixConfig;
};

export type BitrixQueueRow = Pick<
  Database['public']['Tables']['bitrix24_sync_queue']['Row'],
  | 'id'
  | 'source_table'
  | 'source_id'
  | 'idempotency_key'
  | 'status'
  | 'attempts'
  | 'max_attempts'
  | 'next_retry_at'
  | 'last_error_kind'
  | 'last_error_message'
  | 'lead_id'
  | 'created_at'
  | 'updated_at'
>;

export type BitrixLogRow = Pick<
  Database['public']['Tables']['integration_logs']['Row'],
  | 'id'
  | 'integration_name'
  | 'direction'
  | 'action'
  | 'status'
  | 'http_status'
  | 'duration_ms'
  | 'error_message'
  | 'related_entity_type'
  | 'related_entity_id'
  | 'created_at'
>;

export type BitrixTestResult = {
  ok: boolean;
  latency_ms: number | null;
  bitrix_user_id: string | null;
  error_message: string | null;
  http_status: number | null;
};

const SETTINGS_COLUMNS =
  'name, enabled, config, last_test_at, last_test_status, last_test_error, updated_at';

/**
 * Read the bitrix24 row from `integration_settings`. RLS lets admin and
 * super_admin SELECT (R18). Returns the row with a typed `config` shape.
 */
export async function getBitrixSettings(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BitrixSettings | null> {
  const { data, error } = await client
    .from('integration_settings')
    .select(SETTINGS_COLUMNS)
    .eq('name', 'bitrix24')
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    config: data.config as unknown as BitrixConfig,
  };
}

/**
 * Toggle the `enabled` flag on the bitrix24 row. Audited + RLS-gated
 * via the `update_integration_settings` RPC (super_admin only, raises
 * 42501 otherwise).
 */
export async function setBitrixEnabled(
  enabled: boolean,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.rpc('update_integration_settings', {
    p_name: 'bitrix24',
    p_enabled: enabled,
    p_config: undefined,
  });
  if (error) throw error;
}

/**
 * Synchronously invoke the `bitrix24-test-connection` Edge function. The
 * Edge verifies super_admin via service-role, calls Bitrix
 * `user.current.json` directly (single HTTP hop, < 1s typical), updates
 * `integration_settings.last_test_*`, and returns the outcome.
 *
 * This bypasses `pg_net` entirely — the previous RPC-based path was
 * unreliable on Supabase Cloud (`http_collect_response(async := false)`
 * hangs past the PostgREST 8s statement_timeout, observed in W3 hotfix).
 *
 * Auth: the user's JWT is forwarded by `functions.invoke()`; CRON_SECRET
 * never reaches the browser.
 */
export async function testBitrixConnection(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BitrixTestResult> {
  const { data, error } = await client.functions.invoke<BitrixTestResult>(
    'bitrix24-test-connection',
    { body: {} },
  );
  if (error) throw error;
  if (!data) {
    return {
      ok: false,
      latency_ms: null,
      bitrix_user_id: null,
      error_message: 'No response from bitrix24-test-connection',
      http_status: null,
    };
  }
  return {
    ok: Boolean(data.ok),
    latency_ms: data.latency_ms ?? null,
    bitrix_user_id: data.bitrix_user_id ?? null,
    error_message: data.error_message ?? null,
    http_status: data.http_status ?? null,
  };
}

const QUEUE_COLUMNS =
  'id, source_table, source_id, idempotency_key, status, attempts, max_attempts, next_retry_at, last_error_kind, last_error_message, lead_id, created_at, updated_at';

/**
 * List queue rows in a terminal-but-actionable state. `failed_config`
 * means the Edge classified a non-retryable Bitrix error (INVALID_FIELD,
 * unknown UF, etc.) — admin must fix the mapping then retry. `abandoned`
 * means transient retries are exhausted — admin can reset attempts.
 */
export async function listBitrixQueueRows(
  status: 'failed_config' | 'abandoned',
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BitrixQueueRow[]> {
  const { data, error } = await client
    .from('bitrix24_sync_queue')
    .select(QUEUE_COLUMNS)
    .eq('status', status)
    .order('updated_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as BitrixQueueRow[];
}

/**
 * Reset a `failed_config` / `abandoned` row back to `pending` (attempts=0,
 * next_retry_at=NULL) so the next cron tick will re-dispatch. RLS-gated:
 * admin and super_admin only via the RPC.
 */
export async function retryBitrixQueueRow(
  queueId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.rpc('bitrix24_retry_config_error', {
    p_queue_id: queueId,
  });
  if (error) throw error;
}

const LOGS_COLUMNS =
  'id, integration_name, direction, action, status, http_status, duration_ms, error_message, related_entity_type, related_entity_id, created_at';

/**
 * Read the last `limit` rows of `integration_logs` for bitrix24, newest
 * first. Read-only audit panel — admin SELECT-only (R18).
 */
export async function listBitrixLogs(
  limit = 50,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BitrixLogRow[]> {
  const { data, error } = await client
    .from('integration_logs')
    .select(LOGS_COLUMNS)
    .eq('integration_name', 'bitrix24')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data ?? []) as BitrixLogRow[];
}

// Re-export Json for callers that need to introspect raw payload values
// without re-importing from database.types directly.
export type { Json };
